import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { CapabilityProvider } from '@lemontaigu/platform-ui';
import { createSupabaseCapabilityStore } from '@lemontaigu/platform/adapters/client';
import { supabase } from './supabase.js';

/**
 * The capability composition root for the reception desk (ADR-0013, ladder
 * step 8). Named `*Provider.tsx` because that is the convention
 * eslint.config.js matches to recognise a SPA composition root — the one kind
 * of file allowed to bind a concrete client to a port. Sibling of the back-office's `AppCapabilityProvider` — same shape, its own
 * client, which is the point of binding it per app.
 *
 * Mounted inside `AuthGate`: `my_capabilities()` resolves through `auth.uid()`,
 * so it needs a session.
 *
 * This is also where an access MODE would be asserted (migration 0058). The
 * desk sends `X-Capability-Mode: planner` and re-fetches; the database
 * intersects the assertion with `app_mode_entry`, so the header can never
 * unlock more than the desk was already entitled to. Not wired yet — no policy
 * reads `rh.board.assign` until step 9, and turning the UI on before the policy
 * would show a control the database still refuses.
 */
export function AppCapabilityProvider({ children }: { children: ReactNode }) {
  const store = useMemo(() => createSupabaseCapabilityStore(supabase), []);
  return <CapabilityProvider store={store}>{children}</CapabilityProvider>;
}
